import { useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FiEdit2 } from 'react-icons/fi'
import { inventoryService } from '@/lib/inventory/service'
import { getInventoryHealth } from '@/lib/inventory/health'

export default function InventoryDetail() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const kindParam = searchParams.get('kind')
  const kind = kindParam === 'item' || kindParam === 'composite' ? kindParam : undefined
  const backView = kind === 'item' ? 'items' : 'bundles'

  const queryClient = useQueryClient()
  const [isEditingStock, setIsEditingStock] = useState(false)
  const [stockInput, setStockInput] = useState('')
  const [stockError, setStockError] = useState<string | null>(null)

  const { data: item, isLoading } = useQuery({
    queryKey: ['inventory', 'item', id],
    queryFn: async () => {
      if (!id) throw new Error('Missing id')
      return inventoryService.getItemById(id, { kind })
    },
    enabled: !!id,
  })

  const { data: health } = useQuery({
    queryKey: ['inventory', 'health'],
    queryFn: getInventoryHealth,
    staleTime: 60_000,
  })

  const updateStockMutation = useMutation({
    mutationFn: async (newStock: number) => {
      if (!id) throw new Error('Missing id')
      const res = await fetch(`/api/inventory/items/${encodeURIComponent(id)}/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stockOnHand: newStock, kind: kind ?? 'item' }),
      })
      let json: any = null
      try {
        json = await res.json()
      } catch {
        // ignore JSON parse errors
      }
      if (!res.ok || (json && json.error)) {
        const message = (json && (json.message || json.error)) || `Failed to update stock (HTTP ${res.status})`
        throw new Error(message)
      }
      return json
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'item', id] })
    },
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <div className="p-4"><h1 className="text-2xl font-bold">Inventory</h1></div>
        </header>
        <main className="p-4 text-gray-500">Loading...</main>
      </div>
    )
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <div className="p-4"><h1 className="text-2xl font-bold">Inventory</h1></div>
        </header>
        <main className="p-4 text-gray-500">Item not found</main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{item.name}</h1>
          <div className="text-sm text-gray-600">SKU: {item.sku} • ID: {item.externalId}</div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Stock breakdown */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Stock</h2>
            {health?.connected && kind === 'item' ? (
              <button
                type="button"
                aria-label="Edit stock"
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => {
                  setStockError(null)
                  const initial = item.physicalStockOnHand ?? item.stockAvailable ?? 0
                  setStockInput(String(initial))
                  setIsEditingStock(true)
                }}
              >
                <FiEdit2 aria-hidden="true" />
              </button>
            ) : null}
          </div>
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-gray-600">Opening Stock</dt>
              <dd className="text-gray-900">
                {(item.openingStock ?? 0).toFixed(2)}
              </dd>
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Accounting Stock
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <dt className="text-gray-600">Stock on Hand</dt>
                  <dd className="text-gray-900">
                    {(item.accountingStockOnHand ?? item.stockAvailable ?? 0).toFixed(2)}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-600">Committed Stock</dt>
                  <dd className="text-gray-900">
                    {(item.accountingCommittedStock ?? 0).toFixed(2)}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-600">Available for Sale</dt>
                  <dd className="text-gray-900">
                    {(item.accountingAvailableForSale ?? item.stockAvailable ?? 0).toFixed(2)}
                  </dd>
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Physical Stock
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <dt className="text-gray-600">Stock on Hand</dt>
                  <dd className="text-gray-900">
                    {(item.physicalStockOnHand ?? item.stockAvailable ?? 0).toFixed(2)}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-600">Committed Stock</dt>
                  <dd className="text-gray-900">
                    {(item.physicalCommittedStock ?? 0).toFixed(2)}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-gray-600">Available for Sale</dt>
                  <dd className="text-gray-900">
                    {(item.physicalAvailableForSale ?? item.stockAvailable ?? 0).toFixed(2)}
                  </dd>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <dt className="text-gray-600">Reorder Point</dt>
              <dd className="text-gray-900">
                {typeof item.reorderPoint === 'number' ? item.reorderPoint.toFixed(2) : '0.00'}
              </dd>
            </div>
          </dl>

          {isEditingStock && (
            <form
              className="mt-4 border-t border-gray-100 pt-3 space-y-3"
              onSubmit={(e) => {
                e.preventDefault()
                const value = Number(stockInput)
                if (!Number.isFinite(value)) {
                  setStockError('Enter a valid number')
                  return
                }
                setStockError(null)
                updateStockMutation.mutate(value, {
                  onSuccess: () => {
                    setIsEditingStock(false)
                  },
                  onError: (err) => {
                    setStockError(err instanceof Error ? err.message : 'Failed to update stock')
                  },
                })
              }}
            >
              <label className="flex flex-col gap-1 text-xs text-gray-700">
                <span>New stock on hand</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={stockInput}
                  onChange={(e) => setStockInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              {stockError ? <p className="text-xs text-red-600">{stockError}</p> : null}
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium touch-target-min-44 disabled:opacity-60"
                  disabled={updateStockMutation.isPending}
                >
                  {updateStockMutation.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="flex-1 bg-gray-100 text-gray-900 py-2 px-4 rounded-lg text-sm font-medium touch-target-min-44"
                  onClick={() => {
                    setIsEditingStock(false)
                    setStockError(null)
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </section>

        {/* Item meta & pricing */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Item details</h2>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-gray-500">Status</dt>
              <dd className="text-gray-900 mt-0.5">{item.status || '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Unit</dt>
              <dd className="text-gray-900 mt-0.5">{item.unit || 'pcs'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Sales price</dt>
              <dd className="text-gray-900 mt-0.5">
                {typeof item.rate === 'number' ? item.rate.toFixed(2) : '0.00'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Purchase price</dt>
              <dd className="text-gray-900 mt-0.5">
                {typeof item.purchaseRate === 'number' ? item.purchaseRate.toFixed(2) : '0.00'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Created Source</dt>
              <dd className="text-gray-900 mt-0.5">{item.createdSource || 'API'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Inventory Account</dt>
              <dd className="text-gray-900 mt-0.5">{item.inventoryAccountName || 'Inventory Asset'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Inventory Valuation Method</dt>
              <dd className="text-gray-900 mt-0.5">
                {item.valuationMethod || 'FIFO (First In First Out)'}
              </dd>
            </div>
          </dl>
          {item.description ? (
            <p className="mt-3 text-sm text-gray-700 whitespace-pre-line">{item.description}</p>
          ) : null}
        </section>

        {/* Reporting tags */}
        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Reporting Tags</h2>
          {item.reportingTags && item.reportingTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {item.reportingTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              No reporting tag has been associated with this item.
            </p>
          )}
        </section>

        <section className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Components</h2>
          {Array.isArray((item as any).components) && (item as any).components.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {(item as any).components.map((c: any) => (
                <li key={c.itemId} className="py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{c.name || c.itemId}</div>
                    <div className="text-xs text-gray-600">SKU: {c.sku || '—'}</div>
                  </div>
                  <div className="text-sm font-semibold text-gray-900">x{c.quantity}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-600">No components found for this item.</div>
          )}
        </section>

        <div className="flex gap-3 mt-6">
          <Link
            to={`/inventory?view=${backView}`}
            className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium text-center touch-target-min-44 hover:bg-gray-200 transition-colors"
          >
            Back to Inventory
          </Link>
        </div>
      </main>
    </div>
  )
}
